import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { TrendingUp, ShoppingBag, Target, Zap, Settings, LogOut, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('optimexisai_token')}` }
});

export default function Dashboard({ setAuth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedMode, setSelectedMode] = useState('profit_max');
  const [storeForm, setStoreForm] = useState({ type: 'shopify', name: '' });
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadUserData();
    loadStoreData();
    loadProducts();
    loadMetrics();
    loadSuggestions();

    // Check for payment session
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [location]);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setUser(response.data);
    } catch (error) {
      toast.error('Session expired');
      handleLogout();
    }
  };

  const loadStoreData = async () => {
    try {
      const response = await axios.get(`${API}/stores/me`, getAuthHeaders());
      setStore(response.data);
    } catch (error) {
      console.error('Error loading store:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, getAuthHeaders());
      setMetrics(response.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/pricing/suggestions`, getAuthHeaders());
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const handleConnectStore = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/stores/connect?store_type=${storeForm.type}&store_name=${storeForm.name}`,
        {},
        getAuthHeaders()
      );
      setStore(response.data);
      setShowStoreModal(false);
      toast.success('Store connected successfully!');
      loadProducts();
      loadMetrics();
    } catch (error) {
      toast.error('Failed to connect store');
    }
  };

  const toggleMonitor = async (productId, currentState) => {
    try {
      await axios.patch(
        `${API}/products/${productId}/monitor?monitored=${!currentState}`,
        {},
        getAuthHeaders()
      );
      loadProducts();
      toast.success(!currentState ? 'Product monitoring enabled' : 'Product monitoring disabled');
    } catch (error) {
      toast.error('Failed to update monitoring');
    }
  };

  const runPricingAnalysis = async () => {
    if (!store) {
      toast.error('Please connect a store first');
      return;
    }

    const monitoredCount = products.filter(p => p.monitored).length;
    if (monitoredCount === 0) {
      toast.error('Please enable monitoring for at least one product');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/pricing/analyze?mode=${selectedMode}`,
        {},
        getAuthHeaders()
      );
      setSuggestions(response.data.suggestions || []);
      toast.success(`Generated ${response.data.suggestions?.length || 0} pricing suggestions`);
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const applySuggestion = async (suggestionId) => {
    try {
      await axios.post(
        `${API}/pricing/apply/${suggestionId}`,
        {},
        getAuthHeaders()
      );
      toast.success('Price updated successfully!');
      loadProducts();
      loadSuggestions();
      loadMetrics();
    } catch (error) {
      toast.error('Failed to apply suggestion');
    }
  };

  const handleUpgrade = async (plan) => {
    try {
      const response = await axios.post(
        `${API}/subscribe/checkout?plan=${plan}`,
        {},
        getAuthHeaders()
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate checkout');
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setCheckingPayment(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/subscribe/status/${sessionId}`,
          getAuthHeaders()
        );

        if (response.data.status === 'paid' || response.data.status === 'completed') {
          toast.success('Payment successful! Your subscription is now active.');
          loadUserData();
          setCheckingPayment(false);
          // Clear session_id from URL
          window.history.replaceState({}, document.title, '/dashboard');
          return;
        }

        attempts++;
        setTimeout(pollStatus, 2000);
      } catch (error) {
        console.error('Payment status check failed:', error);
        setCheckingPayment(false);
      }
    };

    pollStatus();
  };

  const handleLogout = () => {
    localStorage.removeItem('optimexisai_token');
    localStorage.removeItem('optimexisai_user');
    setAuth(false);
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const monitoredProducts = products.filter(p => p.monitored);

  return (
    <div className="dashboard" data-testid="dashboard">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <div className="logo">
            <div className="pulse-icon"></div>
            <span>OptimexisAi</span>
          </div>
        </div>
        <div className="nav-right">
          <Badge variant="outline" className="subscription-badge" data-testid="subscription-badge">
            {user.subscription_plan === 'trial' ? '7-Day Trial' : user.subscription_plan.toUpperCase()}
          </Badge>
          <Button variant="ghost" size="sm" data-testid="settings-btn">
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </nav>

      <div className="dashboard-content">
        {/* Metrics Cards */}
        {metrics && (
          <div className="metrics-grid" data-testid="metrics-section">
            <Card className="metric-card">
              <div className="metric-icon revenue">
                <TrendingUp />
              </div>
              <div className="metric-content">
                <p className="metric-label">Total Revenue (30d)</p>
                <p className="metric-value" data-testid="metric-revenue">£{metrics.total_revenue.toLocaleString()}</p>
              </div>
            </Card>
            <Card className="metric-card">
              <div className="metric-icon profit">
                <Target />
              </div>
              <div className="metric-content">
                <p className="metric-label">Total Profit (30d)</p>
                <p className="metric-value" data-testid="metric-profit">£{metrics.total_profit.toLocaleString()}</p>
              </div>
            </Card>
            <Card className="metric-card">
              <div className="metric-icon change">
                <Zap />
              </div>
              <div className="metric-content">
                <p className="metric-label">Change vs Last Period</p>
                <p className="metric-value positive" data-testid="metric-change">+{metrics.change_vs_last_period}%</p>
              </div>
            </Card>
            <Card className="metric-card">
              <div className="metric-icon skus">
                <ShoppingBag />
              </div>
              <div className="metric-content">
                <p className="metric-label">Monitored SKUs</p>
                <p className="metric-value" data-testid="metric-skus">{metrics.monitored_skus}</p>
              </div>
            </Card>
          </div>
        )}

        {/* Daily Summary */}
        {metrics && (
          <Card className="summary-card" data-testid="daily-summary">
            <p className="summary-text">{metrics.daily_summary}</p>
          </Card>
        )}

        {/* Store Connection */}
        {!store && (
          <Card className="empty-state" data-testid="empty-state-store">
            <ShoppingBag className="empty-icon" />
            <h3>Connect Your Store</h3>
            <p>Connect your Shopify or WooCommerce store to start optimizing prices</p>
            <Button onClick={() => setShowStoreModal(true)} data-testid="connect-store-btn">
              Connect Store
            </Button>
          </Card>
        )}

        {/* Pricing Analysis */}
        {store && (
          <Card className="analysis-card" data-testid="analysis-section">
            <div className="analysis-header">
              <h3>AI Pricing Optimizer</h3>
              {(user.subscription_plan === 'trial' || user.subscription_plan === 'free') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPricingModal(true)}
                  data-testid="upgrade-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
              )}
            </div>
            <div className="analysis-controls">
              <div className="mode-selector">
                <label>Optimization Mode:</label>
                <Select value={selectedMode} onValueChange={setSelectedMode}>
                  <SelectTrigger data-testid="mode-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profit_max" data-testid="mode-profit-max">Profit Max</SelectItem>
                    <SelectItem value="volume_boost" data-testid="mode-volume-boost">Volume Boost</SelectItem>
                    <SelectItem value="clearance" data-testid="mode-clearance">Clearance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={runPricingAnalysis}
                disabled={analyzing || monitoredProducts.length === 0}
                data-testid="analyze-btn"
              >
                {analyzing ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>
          </Card>
        )}

        {/* Products List */}
        {products.length > 0 && (
          <Card className="products-card" data-testid="products-section">
            <h3>Your Products</h3>
            <div className="products-table">
              <div className="table-header">
                <div>Product</div>
                <div>SKU</div>
                <div>Price</div>
                <div>Stock</div>
                <div>30d Sales</div>
                <div>Monitor</div>
              </div>
              {products.map((product) => (
                <div key={product.id} className="table-row" data-testid={`product-row-${product.sku}`}>
                  <div data-label="Product">{product.name}</div>
                  <div data-label="SKU">{product.sku}</div>
                  <div data-label="Price">£{product.current_price}</div>
                  <div data-label="Stock">{product.stock}</div>
                  <div data-label="30d Sales">{product.sales_30d}</div>
                  <div data-label="Monitor">
                    <Switch
                      checked={product.monitored}
                      onCheckedChange={() => toggleMonitor(product.id, product.monitored)}
                      data-testid={`monitor-switch-${product.sku}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Pricing Suggestions */}
        {suggestions.length > 0 && (
          <Card className="suggestions-card" data-testid="suggestions-section">
            <h3>Pricing Suggestions</h3>
            <div className="suggestions-list">
              {suggestions.map((suggestion) => (
                <div key={suggestion.id} className="suggestion-item" data-testid={`suggestion-${suggestion.sku}`}>
                  <div className="suggestion-header">
                    <div>
                      <h4>{suggestion.product_name}</h4>
                      <p className="suggestion-sku">{suggestion.sku}</p>
                    </div>
                    <Badge className="confidence-badge" data-testid={`confidence-${suggestion.sku}`}>
                      {suggestion.confidence_score}% confidence
                    </Badge>
                  </div>
                  <div className="suggestion-body">
                    <div className="price-change">
                      <span className="old-price">£{suggestion.current_price}</span>
                      <span className="arrow">→</span>
                      <span className="new-price" data-testid={`new-price-${suggestion.sku}`}>£{suggestion.suggested_price}</span>
                    </div>
                    <p className="suggestion-reasoning">{suggestion.reasoning}</p>
                    <p className="suggestion-impact">{suggestion.potential_impact}</p>
                  </div>
                  <Button
                    onClick={() => applySuggestion(suggestion.id)}
                    size="sm"
                    data-testid={`apply-btn-${suggestion.sku}`}
                  >
                    Apply Price
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Store Modal */}
      <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
        <DialogContent data-testid="store-modal">
          <DialogHeader>
            <DialogTitle>Connect Your Store</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConnectStore} className="store-form">
            <div className="form-group">
              <label>Store Type</label>
              <Select value={storeForm.type} onValueChange={(val) => setStoreForm({ ...storeForm, type: val })}>
                <SelectTrigger data-testid="store-type-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shopify">Shopify</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <label>Store Name</label>
              <input
                type="text"
                placeholder="My Amazing Store"
                value={storeForm.name}
                onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                required
                data-testid="store-name-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="store-connect-submit-btn">
              Connect Store (Demo Mode)
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pricing Modal */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="pricing-modal" data-testid="pricing-modal">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
          </DialogHeader>
          <div className="modal-pricing-grid">
            <div className="modal-pricing-card" data-testid="modal-starter-plan">
              <h4>Starter</h4>
              <p className="modal-price">£49/month</p>
              <ul>
                <li>5 SKUs</li>
                <li>Manual approval</li>
              </ul>
              <Button onClick={() => handleUpgrade('starter')} data-testid="upgrade-starter-btn">
                Upgrade to Starter
              </Button>
            </div>
            <div className="modal-pricing-card featured" data-testid="modal-pro-plan">
              <h4>Pro</h4>
              <p className="modal-price">£149/month</p>
              <ul>
                <li>10 SKUs</li>
                <li>Competitor tracking</li>
                <li>Weekly reports</li>
              </ul>
              <Button onClick={() => handleUpgrade('pro')} data-testid="upgrade-pro-btn">
                Upgrade to Pro
              </Button>
            </div>
            <div className="modal-pricing-card" data-testid="modal-growth-plan">
              <h4>Growth</h4>
              <p className="modal-price">£299/month</p>
              <ul>
                <li>Unlimited SKUs</li>
                <li>Auto-apply</li>
                <li>All features</li>
              </ul>
              <Button onClick={() => handleUpgrade('growth')} data-testid="upgrade-growth-btn">
                Upgrade to Growth
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {checkingPayment && (
        <div className="payment-overlay" data-testid="payment-checking">
          <Card className="payment-card">
            <div className="loading-spinner"></div>
            <p>Verifying your payment...</p>
          </Card>
        </div>
      )}
    </div>
  );
}